import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import InfoIcon from "@/components/icons/InfoIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDate } from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { cssVariables, numberOrZero } from "@/utils/utils";
import { Button, Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteAgentBrokerNumber, useGetAgentBrokerNumbers } from "../../../../common/api/queries";
import { PageSizes, TableSizes } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { AgentBrokerNumber } from "../../types";
import AgentBrokerNumberForm from "../forms/AgentBrokerNumberForm";

interface Props {
  agentId: UUID;
  showAllColumns?: boolean;
  showActions?: boolean;
}

export const AgentBrokerNumberTable = ({ agentId, showAllColumns, showActions }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [brokerNumberFormOpen, setBrokerNumberFormOpen] = useState<boolean>(false);
  const [brokerNumberToUpdate, setBrokerNumberToUpdate] = useState<AgentBrokerNumber>();

  const searchParams = useFilterSearchParams();
  const { data: brokerNumbers } = useGetAgentBrokerNumbers(agentId);
  const deleteAgentBrokerNumber = useDeleteAgentBrokerNumber();

  const handleBrokerNumberUpdateClick = (brokerNumber: AgentBrokerNumber) => {
    setBrokerNumberToUpdate(brokerNumber);
    setBrokerNumberFormOpen(true);
  };

  const handleBrokerNumberFormCancel = () => {
    setBrokerNumberToUpdate(undefined);
    setBrokerNumberFormOpen(false);
  };

  const columns: ColumnsType<AgentBrokerNumber> = useMemo(() => {
    const items: ColumnsType<AgentBrokerNumber> = [
      {
        key: "institution",
        title: t("agent.brokerNumber.attrs.institutionId"),
        width: 250,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.institution.name}</Ellipsis>,
      },
      {
        key: "value",
        title: t("agent.brokerNumber.attrs.value"),
        width: 200,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.value}</Ellipsis>,
      },
      {
        key: "validFrom",
        title: t("agent.brokerNumber.attrs.validFrom"),
        width: 100,
        render: (_, record) => (record.validFrom ? formatLocaleDate(record.validFrom) : undefined),
      },
      {
        key: "validTo",
        title: t("agent.brokerNumber.attrs.validTo"),
        width: 100,
        render: (_, record) => (record.validTo ? formatLocaleDate(record.validTo) : undefined),
      },
    ];

    if (showAllColumns) {
      items.push(
        {
          key: "commonUsage",
          title: t("agent.brokerNumber.attrs.commonUsage"),
          align: "center",
          width: 90,
          render: (_, record) => <AntIcon type={record.commonUsage ? "check" : "close-circle"} />,
        },
        {
          key: "useForContractMatching",
          title: t("agent.brokerNumber.attrs.useForContractMatching"),
          align: "center",
          width: 130,
          render: (_, record) => <AntIcon type={record.useForContractMatching ? "check" : "close-circle"} />,
        },
        {
          key: "active",
          title: t("agent.brokerNumber.helpers.active"),
          align: "center",
          width: 70,
          render: (_, record) =>
            (record.validFrom ? dayjs().isSameOrAfter(record.validFrom, "day") : true) &&
            (record.validTo ? dayjs().isSameOrBefore(record.validTo, "day") : true) ? (
              <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
            ) : (
              <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
            ),
        },
        {
          key: "note",
          align: "center",
          width: 30,
          render: (_, record) =>
            record.note ? <InfoIcon title={t("agent.brokerNumber.attrs.note")} tooltip={record.note} /> : null,
        },
      );

      if (showActions) {
        items.push({
          key: "actions",
          align: "right",
          fixed: "right",
          width: 180,
          render: (_, record) => (
            <>
              <ActionTextIcon
                icon="edit"
                color="blue"
                text={t("common.edit")}
                onClick={() => handleBrokerNumberUpdateClick(record)}
              />

              <Divider orientation="vertical" />

              <Popconfirm
                title={t("agent.brokerNumber.helpers.deleteConfirm")}
                icon={<PopconfirmDeleteIcon />}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                okType="danger"
                onConfirm={() => deleteAgentBrokerNumber.mutate({ id1: agentId, id2: record.id })}
              >
                <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
              </Popconfirm>
            </>
          ),
        });
      }
    } else {
      items.push({
        key: "active",
        title: t("agent.brokerNumber.helpers.active"),
        align: "center",
        width: 90,
        render: (_, record) =>
          (record.validFrom ? dayjs().isSameOrAfter(record.validFrom, "day") : true) &&
          (record.validTo ? dayjs().isSameOrBefore(record.validTo, "day") : true) ? (
            <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
          ) : (
            <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
          ),
      });
    }

    return items;
  }, []);

  return (
    <>
      <Table<AgentBrokerNumber>
        {...tableStandardProps()}
        columns={columns}
        dataSource={brokerNumbers}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(searchParams.pageIndex) + 1,
          pageSize: PageSizes.LARGE,
          total: brokerNumbers?.length,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
        scroll={{ x: showAllColumns ? TableSizes.LARGE : TableSizes.MEDIUM }}
      />

      {showActions && (
        <>
          <div className={brokerNumbers?.length ? "table-footer-margin" : "margin-top-small"}>
            <Button
              type="primary"
              size="small"
              icon={<AntIcon type="plus" />}
              onClick={() => setBrokerNumberFormOpen(true)}
            >
              {t("common.add")}
            </Button>
          </div>

          <AgentBrokerNumberForm
            open={brokerNumberFormOpen}
            agentId={agentId}
            brokerNumber={brokerNumberToUpdate}
            onFormCancel={handleBrokerNumberFormCancel}
          />
        </>
      )}
    </>
  );
};
